import React from 'react';
import classNames from 'classnames';
import { X } from 'lucide-react';

import css from './FilterChips.module.css';

/**
 * FilterChips component - Displays active filters as removable chips
 *
 * @component
 * @param {Object} props
 * @param {Object} props.filters - Active filters object
 * @param {Function} props.onRemoveFilter - Callback when a filter is removed (filterKey, value)
 * @param {Function} props.onClearAll - Callback when clear all is clicked
 * @param {Array} [props.listingCategories] - Array of category objects for name lookup
 * @param {Array} [props.filterConfigs] - Array of filter configuration objects to determine filter types
 * @param {string} [props.className] - Custom class name
 * @returns {JSX.Element}
 */
const FilterChips = ({ filters, onRemoveFilter, onClearAll, listingCategories, filterConfigs, className }) => {
  // Filter out empty values and system params
  const systemParams = ['sort', 'page', 'address', 'bounds', 'origin', 'mapSearch'];

  // Helper to get base filter key (remove pub_ or meta_ prefix)
  const getBaseKey = (key) => {
    if (key.startsWith('pub_')) {
      return key.substring(4);
    }
    if (key.startsWith('meta_')) {
      return key.substring(5);
    }
    return key;
  };

  // Helper to check if a filter is a range filter (price or SCHEMA_TYPE_LONG)
  const isRangeFilter = (filterKey) => {
    if (!filterConfigs) return false;

    // Get base key (remove pub_ or meta_ prefix)
    const baseKey = getBaseKey(filterKey);

    // Find the filter config for this key
    const config = filterConfigs.find(f => {
      const configKey = f.key;
      const configScope = f.scope;
      // Check if keys match (with or without scope prefix)
      if (configScope && filterKey.startsWith(`${configScope}_`)) {
        return filterKey === `${configScope}_${configKey}`;
      }
      return baseKey === configKey || filterKey === configKey;
    });

    if (!config) return false;

    // Check if it's a range filter (price or long type)
    return config.schemaType === 'price' || config.schemaType === 'long';
  };

  // Helper function to convert slug to title case
  const slugToTitle = (slug) => {
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper function to find category name by ID (recursive search through nested categories)
  const findCategoryName = (categoryId, categories = []) => {
    if (!categories || !Array.isArray(categories)) return null;

    for (const category of categories) {
      // Check if this is the category we're looking for
      if (category.id === categoryId) {
        return category.name;
      }

      // Recursively search in subcategories
      if (category.subcategories && category.subcategories.length > 0) {
        const found = findCategoryName(categoryId, category.subcategories);
        if (found) return found;
      }
    }

    return null;
  };

  // Value labels mapping
  const valueLabels = {
    listingType: {
      private: 'Private Seller',
      trade: 'Trade Seller',
      'private-seller': 'Private Seller',
      'trade-seller': 'Trade Seller',
      'new-listing-private-seller': 'Private Seller',
      'new-listing-trade-seller': 'Trade Seller',
    },
    transmission: {
      manual: 'Manual',
      automatic: 'Automatic',
      tiptronic: 'Tiptronic',
      semi_automatic: 'Semi-Automatic',
      'semi-automatic': 'Semi-Automatic',
    },
    drivingSide: {
      lhd: 'Left Hand Drive',
      rhd: 'Right Hand Drive',
      left: 'Left Hand Drive',
      right: 'Right Hand Drive',
    },
    bodyStyle: {
      coupe: 'Coupe',
      convertible: 'Convertible',
      targa: 'Targa',
      sedan: 'Sedan',
      suv: 'SUV',
      wagon: 'Wagon',
      hatchback: 'Hatchback',
    },
    location: {
      uk: 'United Kingdom',
      us: 'United States',
      de: 'Germany',
      fr: 'France',
      it: 'Italy',
      es: 'Spain',
      nl: 'Netherlands',
      be: 'Belgium',
      ch: 'Switzerland',
      at: 'Austria',
      'united-kingdom': 'United Kingdom',
      'united-states': 'United States',
      germany: 'Germany',
      france: 'France',
      italy: 'Italy',
      spain: 'Spain',
      netherlands: 'Netherlands',
      belgium: 'Belgium',
      switzerland: 'Switzerland',
      austria: 'Austria',
    },
    category: {
      porsche: 'Porsche',
      ferrari: 'Ferrari',
      lamborghini: 'Lamborghini',
      bmw: 'BMW',
      mercedes: 'Mercedes',
      audi: 'Audi',
    },
  };

  // Build array of individual chips
  const chips = [];

  Object.entries(filters).forEach(([key, value]) => {
    // Skip system params and empty values
    if (systemParams.includes(key) || value == null || value === '') {
      return;
    }

    // Get base key for label lookup (handles pub_ and meta_ prefixes)
    const baseKey = getBaseKey(key);

    // Check if this is a range filter (price, year, mileage, etc.)
    if (isRangeFilter(key) && typeof value === 'string' && value.includes(',')) {
      // For range filters, show as a single chip with "min - max" format
      const [minVal, maxVal] = value.split(',').map(v => v.trim());
      const filterLabel = slugToTitle(baseKey);
      const displayLabel = `${filterLabel}: ${minVal} - ${maxVal}`;

      chips.push({
        key,
        value: value, // Keep the full range as the value
        label: displayLabel,
        chipKey: `${key}-range`,
        isRange: true,
      });
      return;
    }

    // Convert value to array for consistent processing (non-range filters)
    let values = [];
    if (Array.isArray(value)) {
      values = value;
    } else if (typeof value === 'string' && value.includes(',')) {
      // Handle comma-separated strings (e.g., "uk,us,de") - only for non-range filters
      values = value.split(',').map(v => v.trim());
    } else {
      // Single value
      values = [value];
    }

    // Create a chip for each value
    values.forEach((v) => {
      if (v == null || v === '') return;

      // Get display label for this value
      let displayLabel;

      // Check if this is a category filter and we have listingCategories to look up
      if ((baseKey === 'category' || key.includes('category')) && listingCategories) {
        const categoryName = findCategoryName(v, listingCategories);
        if (categoryName) {
          displayLabel = categoryName;
        }
      }

      // If not found in category lookup, try value labels mapping
      if (!displayLabel && valueLabels[baseKey] && valueLabels[baseKey][v]) {
        displayLabel = valueLabels[baseKey][v];
      }

      // If still not found, fallback: convert slug to title case
      if (!displayLabel) {
        displayLabel = slugToTitle(String(v));
      }

      chips.push({
        key,
        value: v,
        label: displayLabel,
        chipKey: `${key}-${v}`,
        isRange: false,
      });
    });
  });

  if (chips.length === 0) {
    return null;
  }

  return (
    <div className={classNames(css.wrapper, className)}>
      <span className={css.label}>Active filters:</span>
      <div className={css.chipsContainer}>
        {chips.map((chip) => (
          <div key={chip.chipKey} className={css.chip}>
            <span className={css.chipLabel}>
              {chip.label}
            </span>
            <button
              type="button"
              className={css.chipRemove}
              onClick={() => onRemoveFilter(chip.key, chip.value)}
              aria-label={`Remove ${chip.label} filter`}
            >
              <X className={css.chipRemoveIcon} />
            </button>
          </div>
        ))}
      </div>
      {onClearAll && (
        <button type="button" className={css.clearAllButton} onClick={onClearAll}>
          Clear all
        </button>
      )}
    </div>
  );
};

export default FilterChips;
